import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/dentlab/src/components/ui/dialog";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { Label } from "@/dentlab/src/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/dentlab/src/components/ui/alert";
import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import { useGermanXmlExport } from "@/dentlab/src/hooks/useGermanXmlExport/useGermanXmlExport";
import { showNotification } from "@/dentlab/src/store/Central/selectors";

interface GermanXmlExportDialogProps {
	open: boolean;
	onClose: () => void;
	jobDocumentId: number;
}

export const GermanXmlExportDialog: React.FC<GermanXmlExportDialogProps> = ({
	open,
	onClose,
	jobDocumentId,
}) => {
	const [auftragsnummer, setAuftragsnummer] = useState("");

	const {
		isLoading,
		exportData,
		validation,
		fetchExportData,
		validateExport,
		executeExport,
	} = useGermanXmlExport();

	useEffect(() => {
		if (open && jobDocumentId) {
			fetchExportData(jobDocumentId);
			setAuftragsnummer("");
		}
	}, [open, jobDocumentId]);

	useEffect(() => {
		if (auftragsnummer && exportData) {
			validateExport(auftragsnummer);
		}
	}, [auftragsnummer, exportData]);

	const handleExport = async () => {
		if (!validation) return;

		const result = await executeExport(auftragsnummer);

		if (result.success) {
			showNotification({
				message: "XML-Export erfolgreich erstellt",
				type: "success",
			});
			onClose();
		} else {
			showNotification({
				message: result.error || "Fehler beim Export",
				type: "error",
			});
		}
	};

	const handleClose = () => {
		setAuftragsnummer("");
		onClose();
	};

	const hasErrors = validation && validation.errors.length > 0;
	const hasWarnings = validation && validation.warnings.length > 0;
	const canExport = !hasErrors && auftragsnummer.trim().length > 0 && auftragsnummer.length <= 50;

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>XML-Export (KZBV)</DialogTitle>
					<DialogDescription>
						Exportieren Sie die Lieferscheindaten im deutschen KZBV-VDZI-VDDS Format.
					</DialogDescription>
				</DialogHeader>

				{isLoading && !exportData ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
					</div>
				) : (
					<div className="grid gap-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="auftragsnummer">Auftragsnummer</Label>
							<Input
								id="auftragsnummer"
								placeholder="Auftragsnummer vom Zahnarzt"
								value={auftragsnummer}
								onChange={(e) => setAuftragsnummer(e.target.value)}
								maxLength={50}
								autoFocus
							/>
							<p className="text-xs text-gray-500">
								{auftragsnummer.length}/50 Zeichen
							</p>
						</div>

						{hasErrors && (
							<Alert variant="destructive">
								<AlertCircle className="h-4 w-4" />
								<AlertTitle>Export nicht möglich</AlertTitle>
								<AlertDescription>
									<ul className="list-disc list-inside mt-2 space-y-1">
										{validation.errors.map((error, index) => (
											<li key={index}>{error}</li>
										))}
									</ul>
								</AlertDescription>
							</Alert>
						)}

						{hasWarnings && (
							<Alert>
								<AlertTriangle className="h-4 w-4" />
								<AlertTitle>Hinweise</AlertTitle>
								<AlertDescription>
									<ul className="list-disc list-inside mt-2 space-y-1">
										{validation.warnings.map((warning, index) => (
											<li key={index}>{warning}</li>
										))}
									</ul>
								</AlertDescription>
							</Alert>
						)}
					</div>
				)}

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={handleClose}
						disabled={isLoading}
					>
						Abbrechen
					</Button>
					<Button
						type="button"
						onClick={handleExport}
						disabled={!canExport || isLoading}
					>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Exportieren...
							</>
						) : hasErrors ? (
							"Export nicht möglich"
						) :	hasWarnings ? (
							"Trotzdem exportieren"
						) : (
							"Exportieren"
						)}	
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
